// Test.tsx
import classNames from 'classnames';
import Button from 'components/Button/Button';
import { BUTTON_COLOR, BUTTON_SIZE } from 'components/Button/Button.type';
import Input from 'components/Input/Input';
import Alert from 'components/Popup/Alert/Alert';
import SelectBox from 'components/SelectBox/SelectBox';
import { CommonOptionType } from 'components/SelectBox/SelectBox.type';
import SingleCalendar from 'components/SingleCalendar/SingleCalendar';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import dayjsPluginUTC from 'dayjs/plugin/utc';
import { useRipeness } from 'hooks/feature/ripeness/useRipeness';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RipenessResponse } from 'types/feature/ripeness/ripeness';
import { setStrLocaleString } from 'utils/common-utils';
import styles from './Ripeness.module.scss';
import { LABEL_VIEW_TYPE, LabelType, RIPENESS_TYPE } from './Ripeness.type';
import { ICON } from 'constants/icons';
import { getSessionStorage } from 'utils/webStorage-utils';
import { WEB_STORAGE_KEYS } from 'constants/webStorageKey';

type Filters = {
    version: 'v2' | 'v3';
    estate?: string;
    userId?: string;
    name?: string;
    date?: string; // 'YYYY-MM-DD'
};

export default function Test() {
    const [showToast, setShowToast] = useState(false);
    const [isShowAlert, setIsShowAlert] = useState(false);

    const [id, setId] = useState<number | undefined>();
    const [date, setDate] = useState('');
    const [userName, setUserName] = useState('');
    const [dateValue, setDateValue] = useState<Date | null>(null);
    const [imgUrl, setImgUrl] = useState<string | undefined>();
    const [isValidated, setIsValidated] = useState<boolean | undefined>(undefined);
    const [isFovPicture, setIsFovPicture] = useState<boolean | undefined>(undefined);
    const [modelType, setModelType] = useState<string>('');

    const [selectedEstate, setSelectedEstate] = useState<CommonOptionType>();
    const [selectedUser, setSelectedUser] = useState<CommonOptionType>();
    const [nameInputValue, setNameInputValue] = useState<string>('');

    const [estateList, setEstateList] = useState<CommonOptionType[]>([]);
    const [userList, setUserList] = useState<CommonOptionType[]>([]);
    const [total, setTotal] = useState(0);
    const [current, setCurrent] = useState(0);

    const [newRipenessType, setNewRipenessType] = useState<LabelType>(RIPENESS_TYPE.UNRIPE);

    const {
        getValidPictureV2,
        getNextValidPictureV2,
        getPrevValidPictureV2,
        deletePicture,
        getEstateList,
        getUserList,
        putLabelPicture,
    } = useRipeness();

    const versionList = [
        { label: 'V3', value: true },
        { label: 'V2', value: false },
    ] as CommonOptionType[];
    const [selectedVersion, setSelectedVersion] = useState<CommonOptionType | undefined>(versionList[0]);

    dayjs.extend(dayjsPluginUTC);
    dayjs.extend(timezone);

    // ------------------------------
    // 1) Filters: 조회 조건 단일 상태
    // ------------------------------
    const [filters, setFilters] = useState<Filters>({
        version: (versionList[0].value ? 'v3' : 'v2') as 'v2' | 'v3',
        estate: undefined,
        userId: undefined,
        name: undefined,
        date: undefined,
    });

    // 버전 변경 → 목록 업데이트 + 필터 리셋만, 조회는 아래 effect가 담당
    useEffect(() => {
        const ver = selectedVersion?.value ? 'v3' : 'v2';

        setSelectedEstate(undefined);
        setSelectedUser(undefined);
        setNameInputValue('');
        setDateValue(null);

        fetchEstateList(ver);
        // user 목록은 estate 바뀔 때 호출되도록 유지

        setFilters({
            version: ver,
            estate: undefined,
            userId: undefined,
            name: undefined,
            date: undefined,
        });
    }, [selectedVersion]); // eslint-disable-line react-hooks/exhaustive-deps

    // estate 바뀌면 user 목록 갱신
    useEffect(() => {
        fetchUserList(filters.version, selectedEstate?.value);
    }, [filters.version, selectedEstate?.value]); // eslint-disable-line react-hooks/exhaustive-deps

    // ------------------------------
    // 2) 목록 로딩
    // ------------------------------
    const fetchEstateList = async (ver: 'v2' | 'v3') => {
        const result = await getEstateList(ver);
        if (result) {
            setEstateList(result.map(estate => ({ label: estate.name, value: estate.code })));
        }
    };

    const fetchUserList = async (ver: 'v2' | 'v3', estate?: string) => {
        const result = await getUserList(ver, estate);
        if (result) {
            setUserList(result.map(user => ({ label: user.name ? user.name : 'null', value: user.id })));
        }
    };

    // ------------------------------
    // 3) 입력 -> filters 업데이트 핸들러
    // ------------------------------
    const onChangeEstate = (opt?: CommonOptionType) => {
        setSelectedEstate(opt);
        setSelectedUser(undefined); // estate 바뀌면 user 선택 초기화
        setFilters(prev => ({ ...prev, estate: opt?.value, userId: undefined }));
    };

    const onChangeUser = (opt?: CommonOptionType) => {
        setSelectedUser(opt);
        setFilters(prev => ({ ...prev, userId: opt?.value as string | undefined }));
    };

    const onChangeName = (val: string) => {
        setNameInputValue(val);
        const cleaned = val.trim();
        setFilters(prev => ({ ...prev, name: cleaned ? cleaned : undefined }));
    };

    const onChangeDate = (d: Date | null) => {
        setDateValue(d);
        setFilters(prev => ({ ...prev, date: d ? dayjs(d).format('YYYY-MM-DD') : undefined }));
    };

    // Enter로 즉시 조회를 원하면 filters를 새 객체로 갱신(동일 값이어도 effect 트리거)
    const handleInputEnterEvent = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            setFilters(prev => ({ ...prev }));
        }
    };

    // ------------------------------
    // 4) 단일 useEffect: 디바운스 + 최신 요청만 반영
    // ------------------------------
    const abortRef = useRef<AbortController | null>(null);
    const reqIdRef = useRef(0);

    useEffect(() => {
        const t = setTimeout(async () => {
            abortRef.current?.abort();
            const aborter = new AbortController();
            abortRef.current = aborter;

            const reqId = ++reqIdRef.current;

            try {
                const result = await getValidPictureV2({
                    isV3: filters.version === 'v3',
                    estate: filters.estate,
                    userId: filters.userId,
                    inputName: filters.name,
                    date: filters.date,
                    // signal: aborter.signal, // 서비스에서 지원하면 주석 해제
                });

                if (reqId === reqIdRef.current) {
                    setPictureData(result);
                    setTotal(result.count.total);
                    setCurrent(result.count.count);

                    if (result.data?.validDate) {
                        setShowToast(true);
                        setTimeout(() => setShowToast(false), 2000);
                    } else if (!result.data) {
                        resetPictureData();
                    }
                }
            } catch (e: any) {
                if (e?.name === 'AbortError') return;
                console.error(e);
            }
        }, 250); // 디바운스 250ms

        return () => clearTimeout(t);
    }, [filters]); // ✅ 오직 filters만 의존

    // ------------------------------
    // 5) 좌/우 이동 및 삭제/확정: 공통 파라미터 사용
    // ------------------------------
    const commonParams = {
        isV3: filters.version === 'v3',
        estate: filters.estate,
        userId: filters.userId,
        inputName: filters.name,
        date: filters.date,
    };

    const fetchNextPicture = async () => {
        if (!id) return;
        try {
            const result = await getNextValidPictureV2({ id, ...commonParams });
            if (result) {
                setPictureData(result);
                setTotal(result.count.total);
                setCurrent(result.count.count);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const fetchPrevPicture = async () => {
        if (!id) return;
        try {
            const result = await getPrevValidPictureV2({ id, ...commonParams });
            if (result) {
                setPictureData(result);
                setTotal(result.count.total);
                setCurrent(result.count.count);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const fetchDeletePicture = async () => {
        if (!id) return;
        try {
            await deletePicture(id);
            setIsShowAlert(false);
            if (current === total) {
                fetchPrevPicture();
            } else {
                fetchNextPicture();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const fetchConfirmPicture = async () => {
        const userId = getSessionStorage(WEB_STORAGE_KEYS.USER_ID);
        if (!id || !userId) return;
        try {
            await putLabelPicture(id, newRipenessType, userId);
            fetchNextPicture();
        } catch (error) {
            console.log(error);
        }
    };

    // ------------------------------
    // 6) 기타 로직(키보드/표시)
    // ------------------------------
    const setPictureData = (response: RipenessResponse) => {
        if (!response.data) {
            resetPictureData();
            return;
        }
        setId(response.data.id);
        setUserName(String(response.data.accountName));
        setDate(dayjs(response.data.createdDate).tz('Asia/Jayapura').format('YYYY.MM.DD HH:mm:ss'));
        setImgUrl(response.data.url);
        setIsValidated(response.data.validDate !== null);
        setIsFovPicture(response.data.fov >= 90);
        setModelType(response.data.model ?? '-');
        setNewRipenessType(response.data.ripeness ?? RIPENESS_TYPE.UNRIPE);
    };

    const resetPictureData = () => {
        setId(undefined);
        setUserName('');
        setIsFovPicture(undefined);
        setModelType('');
        setDate('');
        setImgUrl(undefined);
        setIsValidated(undefined);
    };

    const handleLeftClick = () => {
        if (current === 1) return;
        fetchPrevPicture();
    };

    const handleRightClick = () => {
        if (current === total) return;
        fetchNextPicture();
    };

    const handleDeleteClick = () => setIsShowAlert(true);

    const handleConfirmClick = () => {
        fetchConfirmPicture();
    };

    // 전역 단축키(입력 요소에서는 무시)
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            const tag = (target?.tagName || '').toLowerCase();
            const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable;
            if (isEditable) return;
            if (!(filters.version === 'v3')) return; // v3에만 단축키 적용하려면 유지

            switch (event.key) {
                case '1':
                    setNewRipenessType(RIPENESS_TYPE.UNRIPE);
                    break;
                case '2':
                    setNewRipenessType(RIPENESS_TYPE.RIPE);
                    break;
                // case "3":
                //   setNewRipenessType(RIPENESS_TYPE.OVERRIPE);
                //   break;
                // case "4":
                //   setNewRipenessType(RIPENESS_TYPE.ROTTEN);
                //   break;
                case 'Enter':
                    handleConfirmClick();
                    break;
                case 'ArrowLeft':
                    handleLeftClick();
                    break;
                case 'ArrowRight':
                    handleRightClick();
                    break;
                default:
                    break;
            }
        },
        [filters.version, handleConfirmClick]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // ------------------------------
    // 7) Render
    // ------------------------------
    return (
        <>
            <div className={styles['ripeness__container']}>
                <div className={styles['header']}>
                    <div>
                        <SelectBox
                            selected={selectedVersion}
                            setSelected={setSelectedVersion}
                            placeholder="Select Version"
                            options={versionList}
                        />
                    </div>

                    <div className={styles['header-row']}>
                        <SelectBox
                            selected={selectedEstate}
                            setSelected={onChangeEstate}
                            placeholder="Select Estate"
                            options={estateList}
                            clear
                        />
                        <SingleCalendar date={dateValue} onChangeDate={onChangeDate} resetDate stretch />
                    </div>

                    <div className={styles['header-row']}>
                        <SelectBox
                            selected={selectedUser}
                            setSelected={onChangeUser}
                            placeholder="Select Name"
                            options={userList}
                            flex
                            disabled={nameInputValue.length > 0}
                            clear
                        />
                        <Input
                            value={nameInputValue}
                            onChange={e => onChangeName(e.target.value)}
                            placeholder="Enter user's name"
                            flex
                            disabled={!!selectedUser}
                            onKeyPressDown={handleInputEnterEvent}
                        />
                    </div>
                </div>

                <div
                    className={classNames(styles['body'], selectedVersion?.value ? styles['body--bottom'] : undefined)}
                >
                    <div className={styles['data-info']}>
                        <div className={styles['info']}>
                            <span className={styles['id']}>
                                {id || '-'} ({setStrLocaleString(current, 0)} / {setStrLocaleString(total, 0)})
                                {isFovPicture && (
                                    <div className={styles['is-fov']}>
                                        <img src={ICON.ICON_FOV} width={30} height={30} />
                                    </div>
                                )}
                            </span>
                            <div className={styles['small-info']}>
                                {date === '' && userName === '' && <span>-</span>}
                                {(date !== '' || userName !== '') && (
                                    <>
                                        <span>{date || '-'}</span>
                                        <span>,</span>
                                        <span>{userName || '-'}</span>
                                        {filters.version === 'v3' && (
                                            <>
                                                <span>,</span>
                                                <span>{modelType || '-'}</span>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        {id && (
                            <div>
                                <Button
                                    text="Delete"
                                    size={BUTTON_SIZE[80]}
                                    color={BUTTON_COLOR.black}
                                    onClick={handleDeleteClick}
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles['img-info__wrapper']}>
                        {isValidated && <span className={styles['stamp']}>CONFIRMED</span>}
                        <div className={styles['img-info']}>
                            <button
                                className={classNames(styles['direction-btn'], styles['-left'])}
                                onClick={handleLeftClick}
                            >
                                {current !== 1 && <div className={styles['icon']}></div>}
                            </button>
                            {imgUrl ? <img src={imgUrl} alt="" /> : <span className={styles['noImage']}>No Data</span>}
                            <button
                                className={classNames(styles['direction-btn'], styles['-right'])}
                                onClick={handleRightClick}
                            >
                                {current !== total && <div className={styles['icon']}></div>}
                            </button>
                        </div>
                    </div>
                </div>

                {filters.version === 'v3' && id && (
                    <div className={styles['foot']}>
                        <div className={styles['chip-info']}>
                            <div
                                className={classNames(styles['chip'], {
                                    [styles['-selected']]: newRipenessType === RIPENESS_TYPE.UNRIPE,
                                })}
                                onClick={() => setNewRipenessType(RIPENESS_TYPE.UNRIPE)}
                            >
                                1. {LABEL_VIEW_TYPE.PASS}
                            </div>
                            <div
                                className={classNames(styles['chip'], {
                                    [styles['-selected']]: newRipenessType === RIPENESS_TYPE.RIPE,
                                })}
                                onClick={() => setNewRipenessType(RIPENESS_TYPE.RIPE)}
                            >
                                2. {LABEL_VIEW_TYPE.FAIL1}
                            </div>
                            {/* <div className={classNames(styles["chip"], { [styles["-selected"]]: newRipenessType === RIPENESS_TYPE.OVERRIPE })} onClick={() => setNewRipenessType(RIPENESS_TYPE.OVERRIPE)}>
                3. {LABEL_VIEW_TYPE.FAIL2}
              </div>
              <div className={classNames(styles["chip"], { [styles["-selected"]]: newRipenessType === RIPENESS_TYPE.ROTTEN })} onClick={() => setNewRipenessType(RIPENESS_TYPE.ROTTEN)}>
                4. {LABEL_VIEW_TYPE.FAIL3}
              </div> */}
                        </div>
                        <div className={styles['buttons']}>
                            <Button
                                color={BUTTON_COLOR.blue}
                                size={BUTTON_SIZE[80]}
                                width={'100%'}
                                text="Confirm"
                                onClick={handleConfirmClick}
                            />
                        </div>
                    </div>
                )}
            </div>

            {isShowAlert && (
                <Alert
                    contents={
                        <span>
                            If you delete, you can’t restore it.
                            <br />
                            Do you want to proceed?
                        </span>
                    }
                    onClosePopup={() => setIsShowAlert(false)}
                    firstButton={
                        <Button
                            color={BUTTON_COLOR.white}
                            text="Cancel"
                            onClick={() => setIsShowAlert(false)}
                            width={120}
                        />
                    }
                    secondButton={
                        <Button color={BUTTON_COLOR.black} text="Confirm" onClick={() => fetchDeletePicture()} />
                    }
                />
            )}
        </>
    );
}
